import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FolderLock, KeyRound, Landmark, Plus, Search, Settings2 } from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { BitacoraCredencial } from "@/componentes/credenciales/BitacoraCredencial"
import { CategoriasCofre } from "@/componentes/credenciales/CategoriasCofre"
import { EstadoDelCofre } from "@/componentes/credenciales/EstadoDelCofre"
import { FormularioCredencial } from "@/componentes/credenciales/FormularioCredencial"
import { PedirClave } from "@/componentes/credenciales/PedirClave"
import { PinesBancarios } from "@/componentes/credenciales/PinesBancarios"
import { TarjetaCredencial } from "@/componentes/credenciales/TarjetaCredencial"
import type {
  Categoria,
  Credencial,
  EstadoCofre,
  PaginaCredenciales,
  Revelada,
} from "@/componentes/credenciales/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

const POR_PAGINA = 24

/** Lo que dura una clave a la vista antes de volver sola a la máscara. */
const VISIBLE_MS = 20_000

/**
 * El cofre de credenciales: los accesos de la agencia a portales, bancos y plataformas.
 *
 * ⚠ **Lo que se ve lo decide la CATEGORÍA.** El servidor sólo manda las fichas de las
 * categorías que abren los roles de quien mira; las demás no llegan, ni con candado.
 *
 * ⚠ **Mostrar una clave pide la contraseña del sistema** la primera vez, y el cofre
 * queda abierto 5 minutos para esta sesión. La clave vuelve sola a la máscara a los 20
 * segundos, y al salir de la pantalla no queda ninguna a la vista.
 */
export function Credenciales() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "credencial:editar")
  const puedeAdministrar = tienePermiso(usuario, "credencial:administrar")
  // Los PIN de los bancos: quien administra el cofre Y las cuentas (ver `pin_bancos.py`).
  const puedePines = puedeAdministrar && tienePermiso(usuario, "tesoreria:administrar")
  const [viendoPines, setViendoPines] = useState(false)

  const [categoriaId, setCategoriaId] = useState<number | null>(null)
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")
  const [desde, setDesde] = useState(0)
  const [reveladas, setReveladas] = useState<Record<number, Revelada>>({})
  const [pidiendoPara, setPidiendoPara] = useState<number | null>(null)
  const [editando, setEditando] = useState<Credencial | "nueva" | null>(null)
  const [bitacoraDe, setBitacoraDe] = useState<Credencial | null>(null)
  const [borrando, setBorrando] = useState<Credencial | null>(null)
  const [gestionando, setGestionando] = useState(false)
  const relojes = useRef(new Map<number, number>())

  // La búsqueda espera a que se deje de teclear: una petición por letra, no.
  useEffect(() => {
    const espera = window.setTimeout(() => {
      setBuscar(texto.trim())
      setDesde(0)
    }, 300)
    return () => window.clearTimeout(espera)
  }, [texto])

  // Al salir de la pantalla no queda ningún reloj vivo.
  useEffect(() => {
    const vivos = relojes.current
    return () => {
      for (const reloj of vivos.values()) window.clearTimeout(reloj)
    }
  }, [])

  const cofre = useQuery({
    queryKey: ["credenciales", "cofre"],
    queryFn: () => api.get<EstadoCofre>("/api/v1/credenciales/cofre"),
  })
  const categorias = useQuery({
    queryKey: ["credenciales", "categorias"],
    queryFn: () => api.get<Categoria[]>("/api/v1/credenciales/categorias"),
  })

  const parametros = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(desde) })
  if (categoriaId !== null) parametros.set("categoria_id", String(categoriaId))
  if (buscar !== "") parametros.set("buscar", buscar)
  const lista = useQuery({
    queryKey: ["credenciales", "lista", { categoriaId, buscar, desde }],
    queryFn: () => api.get<PaginaCredenciales>(`/api/v1/credenciales?${parametros.toString()}`),
    placeholderData: keepPreviousData,
  })

  function ocultar(id: number) {
    window.clearTimeout(relojes.current.get(id))
    relojes.current.delete(id)
    setReveladas((previas) => {
      const { [id]: _fuera, ...resto } = previas
      return resto
    })
  }

  function mostrar(id: number, datos: Revelada) {
    window.clearTimeout(relojes.current.get(id))
    setReveladas((previas) => ({ ...previas, [id]: datos }))
    relojes.current.set(
      id,
      window.setTimeout(() => ocultar(id), VISIBLE_MS),
    )
  }

  const revelar = useMutation({
    mutationFn: ({ id, password }: { id: number; password?: string }) =>
      api.post<Revelada>(
        `/api/v1/credenciales/${id}/revelar`,
        password === undefined ? {} : { password },
      ),
    onSuccess: (datos, { id }) => {
      setPidiendoPara(null)
      mostrar(id, datos)
      void clienteQuery.invalidateQueries({ queryKey: ["credenciales", "cofre"] })
    },
    onError: (error, { id }) => {
      if (error instanceof ErrorApi && error.detalles?.motivo === "COFRE_CERRADO") {
        setPidiendoPara(id)
      }
    },
  })

  const cerrarCofre = useMutation({
    mutationFn: () => api.delete("/api/v1/credenciales/cofre"),
    onSuccess: () => {
      for (const id of Object.keys(reveladas)) ocultar(Number(id))
      void clienteQuery.invalidateQueries({ queryKey: ["credenciales", "cofre"] })
    },
  })

  const borrar = useMutation({
    mutationFn: (credencial: Credencial) => api.delete(`/api/v1/credenciales/${credencial.id}`),
    onSuccess: (_, credencial) => {
      ocultar(credencial.id)
      setBorrando(null)
      void clienteQuery.invalidateQueries({ queryKey: ["credenciales"] })
    },
  })

  const falloRevelar =
    revelar.error instanceof ErrorApi &&
    pidiendoPara === null &&
    revelar.error.detalles?.motivo !== "COFRE_CERRADO"
      ? revelar.error.message
      : null

  const visibles = categorias.data ?? []
  const items = lista.data?.items ?? []
  const total = lista.data?.total ?? 0

  function elegirCategoria(id: number | null) {
    setCategoriaId(id)
    setDesde(0)
  }

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <KeyRound size={20} aria-hidden={true} />
            Credenciales
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Los accesos de la agencia a portales, bancos y plataformas. Las contraseñas se guardan
            cifradas; verlas pide tu contraseña del sistema y queda en la bitácora.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {puedePines && (
            <Boton onClick={() => setViendoPines(true)}>
              <Landmark size={16} aria-hidden={true} />
              PIN de bancos
            </Boton>
          )}
          {puedeAdministrar && (
            <Boton onClick={() => setGestionando(true)}>
              <Settings2 size={16} aria-hidden={true} />
              Categorías y accesos
            </Boton>
          )}
          {puedeEditar && visibles.length > 0 && cofre.data?.configurado !== false && (
            <Boton variante="primario" onClick={() => setEditando("nueva")}>
              <Plus size={16} aria-hidden={true} />
              Nueva credencial
            </Boton>
          )}
        </div>
      </header>

      <EstadoDelCofre
        estado={cofre.data}
        pedidoEn={cofre.dataUpdatedAt}
        cerrando={cerrarCofre.isPending}
        onCerrar={() => cerrarCofre.mutate()}
      />

      {visibles.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
          <fieldset className="flex flex-wrap gap-2">
            <legend className="sr-only">Categorías</legend>
            <button
              type="button"
              aria-pressed={categoriaId === null}
              onClick={() => elegirCategoria(null)}
              className={
                categoriaId === null
                  ? "boton-primario h-9 px-3 text-sm"
                  : "boton-secundario h-9 px-3 text-sm"
              }
            >
              Todas
            </button>
            {visibles.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={categoriaId === c.id}
                onClick={() => elegirCategoria(c.id)}
                className={
                  categoriaId === c.id
                    ? "boton-primario h-9 px-3 text-sm"
                    : "boton-secundario h-9 px-3 text-sm"
                }
              >
                {c.nombre} <span className="tabular-nums">({c.credenciales})</span>
              </button>
            ))}
          </fieldset>
          <label className="relative md:ml-auto md:w-72">
            <span className="sr-only">Buscar</span>
            <Search
              size={16}
              aria-hidden={true}
              className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              style={{ color: "var(--fg-muted)" }}
            />
            <input
              type="search"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              maxLength={80}
              placeholder="Buscar por nombre, usuario o web"
              className="campo pl-8"
            />
          </label>
        </div>
      )}

      {falloRevelar !== null && (
        <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
          {falloRevelar}
        </p>
      )}

      {categorias.isPending || lista.isPending ? (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      ) : categorias.isError || lista.isError ? (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el cofre: {(categorias.error ?? lista.error)?.message}
        </p>
      ) : visibles.length === 0 ? (
        <Vacio>
          {puedeAdministrar
            ? "Todavía no hay categorías. Crea la primera en «Categorías y accesos»."
            : "No tienes ninguna categoría del cofre asignada. Pide acceso a quien lo administra."}
        </Vacio>
      ) : items.length === 0 ? (
        <Vacio>
          {buscar === "" ? "Todavía no hay credenciales aquí." : "Nada coincide con la búsqueda."}
        </Vacio>
      ) : (
        <>
          <div className="grid gap-3 lg:grid-cols-2">
            {items.map((c) => (
              <TarjetaCredencial
                key={c.id}
                credencial={c}
                revelada={reveladas[c.id]}
                revelando={revelar.isPending && revelar.variables?.id === c.id}
                // La copia del PIN de una cuenta se cambia en «PIN de bancos», no aquí.
                puedeEditar={puedeEditar && c.cuenta_bancaria_id === null}
                onRevelar={() => revelar.mutate({ id: c.id })}
                onOcultar={() => ocultar(c.id)}
                onEditar={() => setEditando(c)}
                onBorrar={() => setBorrando(c)}
                onBitacora={() => setBitacoraDe(c)}
              />
            ))}
          </div>
          {total > POR_PAGINA && (
            <div
              className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm"
              style={{ color: "var(--fg-muted)" }}
            >
              <span className="tabular-nums">
                {desde + 1}–{desde + items.length} de {total}
              </span>
              <div className="flex gap-2">
                <Boton
                  onClick={() => setDesde(Math.max(0, desde - POR_PAGINA))}
                  disabled={desde === 0}
                >
                  Anteriores
                </Boton>
                <Boton
                  onClick={() => setDesde(desde + POR_PAGINA)}
                  disabled={desde + POR_PAGINA >= total}
                >
                  Siguientes
                </Boton>
              </div>
            </div>
          )}
        </>
      )}

      <PedirClave
        abierto={pidiendoPara !== null}
        error={revelar.error}
        pendiente={revelar.isPending}
        onEnviar={(password) => {
          if (pidiendoPara !== null) revelar.mutate({ id: pidiendoPara, password })
        }}
        onCerrar={() => {
          setPidiendoPara(null)
          revelar.reset()
        }}
      />

      {editando !== null && (
        <FormularioCredencial
          credencial={editando === "nueva" ? null : editando}
          categorias={visibles}
          categoriaPorOmision={categoriaId}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null)
            void clienteQuery.invalidateQueries({ queryKey: ["credenciales"] })
          }}
        />
      )}

      <BitacoraCredencial credencial={bitacoraDe} onCerrar={() => setBitacoraDe(null)} />

      {puedePines && (
        <PinesBancarios abierto={viendoPines} onCerrar={() => setViendoPines(false)} />
      )}

      {puedeAdministrar && (
        <CategoriasCofre
          abierto={gestionando}
          categorias={visibles}
          onCerrar={() => setGestionando(false)}
        />
      )}

      <Modal
        abierto={borrando !== null}
        titulo="Borrar la credencial"
        descripcion="Se borra de verdad: la contraseña guardada desaparece. La bitácora conserva quién la registró, quién la vio y quién la borró."
        onCerrar={() => setBorrando(null)}
        ancho="sm"
      >
        <p className="text-sm">¿Borrar «{borrando?.nombre}»?</p>
        {borrar.isError && (
          <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
            {borrar.error instanceof ErrorApi ? borrar.error.message : "No se pudo borrar"}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Boton variante="fantasma" onClick={() => setBorrando(null)}>
            Cancelar
          </Boton>
          <Boton
            variante="peligro"
            onClick={() => borrando !== null && borrar.mutate(borrando)}
            disabled={borrar.isPending}
          >
            Borrar
          </Boton>
        </div>
      </Modal>
    </div>
  )
}

function Vacio({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex flex-col items-center gap-2 superficie px-4 py-12 text-center"
      style={{ color: "var(--fg-muted)" }}
    >
      <FolderLock size={22} aria-hidden={true} />
      <p className="text-sm">{children}</p>
    </div>
  )
}
